<template>
  <v-layout column>
    <!-- Signature Print Info Dialog -->
    <v-dialog
      v-model="isSignatureModalVisible"
      width="480"
      class="hide-on-print"
      @keydown.esc="isSignatureModalVisible = false"
    >
      <v-card block flat class="hide-on-print">
        <v-card-title class="title">
          {{ $t('questionnaire.signTitle') }}
        </v-card-title>
        <v-card-text>
          <div class="agreement_signature_section__step_container">
            <div class="agreement_signature_section__step_to_upload">
              <span>1</span>
              <sx-small>{{ $t('questionnaire.signTooltip1') }}</sx-small>
            </div>
            <div class="agreement_signature_section__step_to_upload">
              <span>2</span>
              <sx-small>{{ $t('questionnaire.signTooltip2') }}</sx-small>
            </div>
            <div class="agreement_signature_section__step_to_upload">
              <span>3</span>
              <sx-small>{{ $t('questionnaire.signTooltip3') }}</sx-small>
            </div>
            <div class="agreement_signature_section__step_to_upload">
              <span>4</span>
              <div v-if="isFirmCommunicationDisabled">
                <sx-small>{{ $t('questionnaire.signTooltip5') }}</sx-small>
              </div>
              <div v-else>
                <sx-button-group>
                  <div @click="openRelevantConversation()">
                    <sx-button type="secondary" size="small">
                      {{ $t('questionnaire.signTooltip4') }}
                    </sx-button>
                  </div>
                </sx-button-group>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <sx-button-group x-align="center" :is-full-width="true">
            <div @click="isSignatureModalVisible = false">
              <sx-button type="secondary">
                {{ $t('cancel') }}
              </sx-button>
            </div>
            <div @click="agreementDownload()">
              <sx-button type="primary">
                <sx-icon name="print" :size="16" />
                {{ $t('questionnaire.signTitle') }}
              </sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Letter Content -->
    <v-flex
      class="frame text-xs-left"
      :class="$vuetify.breakpoint.smAndDown ? 'pa-2 mb-2' : 'pa-4 mb-4'"
      :style="
        $vuetify.breakpoint.smAndDown
          ? 'max-height: 300px'
          : 'max-height: 500px'
      "
    >
      <sx-html :text="currentSection.custom_element" />
      <div v-if="!hasSavedSignature">
        <br />
        <br />
        <div class="show-on-print agreement_signature_section">
          <div class="agreement_signature_section__spacer" />
          <sx-small
            ><b>{{ currentUser.fname }}, {{ currentUser.lname }}</b></sx-small
          >
          <sx-small>{{ moment().format('ll') }}</sx-small>
        </div>
      </div>
    </v-flex>

    <!-- Last Reviewed -->
    <span
      v-if="currentQuestionnaire.engagement_letter_revision_date"
      class="font-weight-bold text-xs-right mb-2"
      >Last Revised
      {{
        moment(currentQuestionnaire.engagement_letter_revision_date).format(
          'll',
        )
      }}</span
    >

    <!-- Main Container -->
    <v-layout
      column
      class="main-container"
      :class="
        showUnanswered && !currentSection.marked_completed
          ? 'outlined-red px-3'
          : ''
      "
    >
      <!-- Preview -->
      <v-layout row wrap justify-center v-if="preview">
        <v-flex xs12 md8 lg4>
          <v-layout column>
            <label class="font-weight-bold subheading"> Client Name </label>
            <div class="my-2 dark-grey--text">
              {{ $t('questionnaire.unsigned') }}
            </div>
            <v-tooltip bottom>
              <v-text-field
                slot="activator"
                :label="$t('questionnaire.signature')"
                class="default-input"
                solo
                disabled
              />
              <span>You cannot sign in preview</span>
            </v-tooltip>
            <v-btn outline class="default-input primary--text text-none">
              Mark Not Needed
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>

      <!-- Live -->
      <v-layout row wrap justify-center v-else>
        <v-flex
          xs12
          md8
          lg4
          v-for="signature_box in currentSection.engagement_signatures"
          :key="signature_box.id"
          class="mx-4"
        >
          <sc-flex :is-column="true" :gap="12">
            <sc-flex
              v-if="!signature_box.signature"
              :class="!signature_box.signature && 'hide-on-print'"
              class="signature_container"
              @click="onSignatureClick()"
            >
              <sc-icon name="close" size="small"></sc-icon>
              <div class="signature_line"></div>
            </sc-flex>
            <div
              v-else-if="signature_box.signature === 'Marked Not Needed'"
              class="darkgrey--text"
            >
              {{
                $t('questionnaire.signatureNotNeeded', {
                  user_name: signature_box.user_name,
                })
              }}
              <v-icon
                class="error--text ml-2"
                size="18"
                style="cursor: pointer"
                @click="unmarkNotNeeded(signature_box)"
                v-if="currentUser.is_advisor"
                >cancel</v-icon
              >
            </div>
            <div v-else class="signature">
              <span class="ph-no-capture">{{ signature_box.signature }}</span>
            </div>
            <div
              v-if="!currentUser.is_advisor && !signature_box.signature"
              class="my-2 dark-grey--text hide-on-print"
            >
              <v-text-field
                v-if="
                  signature_box.user_id === currentUser.id &&
                    currentUserSignature &&
                    !currentUserSignature.signature
                "
                :rules="signatureRules"
                v-model="signatureToSubmit"
                :label="$t('questionnaire.signature')"
                class="default-input"
                id="signature-to-submit"
                :class="isSignatureHighlighted ? 'default-input--active' : ''"
                solo
              />
              <sx-tiny
                color="light-gray"
                v-if="
                  signature_box.user_id === currentUser.id &&
                    currentUserSignature &&
                    !currentUserSignature.signature
                "
                >{{ $t('questionnaire.signDescription') }}
                <span
                  @click="isSignatureModalVisible = true"
                  class="cursor-pointer primary--text font-weight-bold"
                  >{{ $t('questionnaire.signPrinted') }}.</span
                >
              </sx-tiny>
              <sx-tiny color="light-gray" v-else
                >{{
                  requireOneSignaturePerSection
                    ? $t('questionnaire.onlyOneSignatureRequired')
                    : $t('questionnaire.requiredToSign', {
                        user_name: signature_box.user_name,
                        user_email: currentCompany.owners.find(
                          owner => owner.id === signature_box.user_id,
                        ).email,
                      })
                }}
              </sx-tiny>
            </div>
            <!-- Action: Mark not needed. -->
            <v-btn
              v-else-if="currentUser.is_advisor && !signature_box.signature"
              @click="markNotNeeded(signature_box)"
              outline
              class="default-input primary--text text-none"
            >
              Mark Not Needed
            </v-btn>
            <!-- Marked not needed -->
            <div v-else class="my-2">
              <div>
                <div
                  class="darkgrey--text"
                  v-if="signature_box.signature !== 'Marked Not Needed'"
                >
                  {{ $t('questionnaire.signed') }}:
                  {{ moment(signature_box.signed_at).format('ll')
                  }}<span class="hide-on-print">
                    &mdash;
                    <a
                      @click="agreementDownload()"
                      class="cursor-pointer darkgrey--text"
                      >{{ $t('questionnaire.signature_download') }}</a
                    ></span
                  >
                </div>
                <v-btn
                  @click="unmarkNotNeeded(signature_box)"
                  outline
                  class="default-input error--text text-none mb-4"
                  v-if="
                    currentUser.is_advisor &&
                      signature_box.signature !== 'Marked Not Needed'
                  "
                >
                  Remove Signature
                </v-btn>
              </div>
            </div>
          </sc-flex>
        </v-flex>
      </v-layout>

      <!-- Sign Action -->
      <sc-button-group
        align="center"
        :is-full-width="true"
        v-if="currentUserSignature && !currentUserSignature.signature"
        class="hide-on-print"
      >
        <sc-button
          @click="signEngagementLetter()"
          size="normal"
          icon-left="nib"
          type="primary"
          :is-loading="engagementLoading"
          :is-disabled="isSignDisabled"
        >
          {{ $t('questionnaire.sign') }}
        </sc-button>
      </sc-button-group>
      <br />
      <br />
      <sc-hr></sc-hr>
    </v-layout>
  </v-layout>
</template>

<script>
import * as Sentry from '@sentry/vue';
import isEmpty from 'lodash/isEmpty';
import FirmPreferenceMixins from '../../mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';
import API from '@/api';
import moment from 'moment';
// import posthog from 'posthog-js';
import SxIcon from '@/components/sx/SxIcon';
import SxTiny from '@/components/sx/SxTiny';
import SxSmall from '@/components/sx/SxSmall';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';
import SxHtml from '@/components/sx/SxHtml';

export default {
  props: {
    currentQuestionnaire: {
      type: Object,
      default: () => {},
    },
    currentSection: {
      type: Object,
      default: () => {},
    },
    preview: {
      type: Boolean,
      default: false,
    },
    showUnanswered: {
      type: Boolean,
      default: false,
    },
    isFirmCommunicationDisabled: {
      type: Boolean,
      default: false,
    },
    openRelevantConversation: {
      type: Function,
      default: () => null,
    },
    currentCompany: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    SxIcon,
    SxButton,
    SxButtonGroup,
    SxTiny,
    SxSmall,
    SxHtml,
  },
  mixins: [FirmPreferenceMixins],
  data() {
    return {
      isSignatureModalVisible: false,
      isSignatureHighlighted: false,
      isChecked: false,
      engagementLoading: false,
      signatureToSubmit: '',
      signatureRules: [
        v => !!v || this.$t('questionnaire.pleaseInputFullName'),
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    isSignDisabled() {
      return (
        isEmpty(this.signatureToSubmit) ||
        !this.currentUserSignature ||
        this.preview
      );
    },
    hasSavedSignature() {
      const hasSavedSignature = Boolean(
        this.currentSection.engagement_signatures.length &&
          this.currentSection.engagement_signatures[0] &&
          this.currentSection.engagement_signatures[0].id,
      );

      return hasSavedSignature;
    },
    currentUserSignature() {
      if (this.currentSection.engagement_signatures) {
        return this.currentSection.engagement_signatures.find(
          signature => signature.user_id === this.currentUser.id,
        );
      } else {
        return null;
      }
    },
  },
  methods: {
    isEmpty,

    agreementDownload() {
      globalThis.print();
    },

    // Highlight the signature area if it's empty and someone clicks on signature line (x____).
    onSignatureClick() {
      if (this.signatureToSubmit) return;

      const inputRef = document.getElementById('signature-to-submit');

      this.isSignatureHighlighted = true;
      inputRef && inputRef.focus();
    },

    moment,
    async unmarkNotNeeded(signatureBox) {
      this.engagementLoading = true;
      await API.questionnaire.removeSignature({
        signatureId: signatureBox.id,
        questionnaireId: this.currentQuestionnaire.id,
        companyId: this.currentQuestionnaire.company_id,
      });
      this.$emit('refresh-questionnaire');

      this.engagementLoading = false;
    },
    async markNotNeeded(signatureBox) {
      try {
        this.engagementLoading = true;
        if (!this.preview) {
          signatureBox.signature = 'Marked Not Needed';
          await API.questionnaire.signAgreement({
            questionnaireId: this.currentQuestionnaire.id,
            companyId: this.currentQuestionnaire.company_id,
            signature: 'Marked Not Needed',
            signatureId: signatureBox.id,
          });
          this.$emit('refresh-questionnaire');
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Successfully Marked Not Needed',
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem with the request.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.engagementLoading = false;
      }
    },
    async signEngagementLetter() {
      if (this.isSignDisabled) return;
      this.$emit('update-is-signing-agreement', true);

      try {
        this.engagementLoading = true;

        const params = {
          signatureId: this.currentUserSignature.id,
          questionnaireId: this.currentQuestionnaire.id,
          companyId: this.currentQuestionnaire.company_id,
          signature: this.signatureToSubmit,
          clientTime: new Date().toISOString(),
          userAgent: navigator.userAgent,
        };

        await API.questionnaire.signAgreement(params);

        this.$emit('refresh-questionnaire');

        // posthog.capture('signed engagement letter', {
        //   questionnaire_id: this.currentQuestionnaire.id,
        // });

        this.$store.dispatch('application/setSuccessNotification', {
          message: this.$t('questionnaire.successfullySigned'),
        });
      } catch (err) {
        this.$emit('update-is-signing-agreement', false);
        console.error(err);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem saving the signature.',
          errorToSend: err,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.$emit('update-is-signing-agreement', false);
        this.engagementLoading = false;
      }
    },
  },
  watch: {
    signatureToSubmit: {
      async handler() {
        this.isSignatureHighlighted = false;
      },
      immediate: true,
    },
  },
};
</script>

<style lang="postcss" scoped>
.frame {
  overflow: auto;

  background-color: var(--gray-0);
  border: 1px solid var(--gray-200);
  border-radius: 2px;
}

.frame::-webkit-scrollbar {
  -webkit-appearance: none;
}

.frame::-webkit-scrollbar:vertical {
  width: 11px;
}

.frame::-webkit-scrollbar:horizontal {
  height: 11px;
}

.frame::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 2px solid white; /* should match background, can't be transparent */
  background-color: rgba(0, 0, 0, 0.5);
}

.main-container {
  gap: 16px;
}

/* NOTE @chris This is to highlight the input after we moved focus to it, will be replaced with a flag on sc-input:focus. */
.default-input--active {
  border-radius: 4px;

  outline: 3px solid var(--blue-500) !important;
  outline-offset: 2px;
}

.signature_container {
  display: flex;
  padding-top: 24px;
  justify-content: center;
}

.signature {
  display: inline-flex;
  justify-content: center;

  font-family: 'Cedarville Cursive', cursive;
  font-size: 22px;
  text-align: center;
}

.signature_line {
  padding-top: 24px;
  height: 0;
  width: 240px;

  border-bottom: 1px dashed var(--gray-500);
}
.outlined-red {
  border: 1px solid #cb4039;
}

.cursor-pointer {
  cursor: pointer !important;
}

.agreement_signature_section__step_container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agreement_signature_section__step_to_upload {
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 6px 0;
}

.agreement_signature_section__step_to_upload:not(:last-child) {
  border-bottom: 1px solid var(--gray-50);
}

.agreement_signature_section__step_to_upload span {
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  min-width: 32px;
  width: 32px;

  background-color: var(--blue-100);
  border-radius: 100%;
  color: var(--blue-500);

  font-weight: 500;
}

.agreement_signature_section {
  gap: 12px;
  padding: 0;
  margin: 0;
  max-width: 33%;

  border-top: 1px solid #222;
}

@media print {
  .frame {
    display: block !important;
    border: 0;
    padding: 0 !important;
  }
}
</style>
